/** Material UI Packages. */
import { randomId } from '@mui/x-data-grid-generator';

/** Redux Slices. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Dompurify Packages. */
import DOMPurify from 'dompurify';

/** Date & Time converting library. */
import dayjs from 'dayjs';

/** Internal Components. */
import { readSocketData, createSocketData, deleteSocketData, updateSocketData } from 'shared/lib/fetchSocket';
import { applySocketDelete } from 'shared/lib/socketHandler';
import { deriveEditAssociateValue } from './StatusForm';
import {
    combineOperationDateTime,
    formatDateToDateString,
    parseDateToLocal,
    toOperationTime,
} from 'shared/lib/dateFormatter';

/** Server error code for a status entry that no longer exists (a colleague already deleted it). */
const STATUS_NOT_FOUND = 'STATUS_NOT_FOUND';

// export const transformApiResponse = (apiDataArray) => {
//     return apiDataArray
//         ?.map((apiData) => {
//             if (apiData.cntr_number) {
//                 return {
//                     id: apiData.id,
//                     content: apiData.cntr_number,
//                 };
//             } else if (apiData.alias) {
//                 return {
//                     id: apiData.id,
//                     content: apiData.alias,
//                 };
//             }
//             return null;
//         })
//         .filter((item) => item !== null);
// };

/** The one container the manager picked for this status; null when none or several are selected. */
const selectedSingleChildId = (childId) => {
    const list = Array.isArray(childId) ? childId : [childId];
    if (list.length !== 1) return null;
    const [first] = list;
    const id = typeof first === 'object' && first !== null ? first.id : first;
    return Number(id) > 0 ? Number(id) : null;
};

/**
 * The split (alias) a status is saved under, as the manager meant it.
 *
 * A manager can only put a status on a split after picking that split's container, so a
 * split pick counts only while exactly one container is selected. Putting the status on
 * several containers ("Select all") or on none records a container-level status
 * (alias_id 0) even if a split was picked earlier — the split field is hidden then, so the
 * manager cannot see the stale pick. Whether the split really belongs to the selected
 * container is checked on the server, which holds the split rows.
 */
export const resolveAliasId = (childId, aliasId) => {
    const aid = Number(aliasId);
    if (!aid) return 0;
    return selectedSingleChildId(childId) === null ? 0 : aid;
};

export const createStatusHandlers = ({
    status,
    setStatus,
    setHasAlias,
    setStatusOptions,
    setAssociateValue,
    setIsEdit,
    setShow,
    setHistoryList,
    setIndexRef,
    setHistoryAnchorEl,
    setLocationList,
    cursorRef,
    setHasPrev,
    row,
    tabValue,
    statusConfig,
    selectedHouseRow,
    statusList,
    dispatch,
    initialStatus,
    isEdit,
}) => {
    /** Handle to change event date.  */
    const handleEventDateChange = (date) => {
        setStatus((prev) => ({ ...prev, event_date: date }));
    };
    /** Handle to change DATE. */
    const handleDateChange = (date) => {
        setStatus((prev) => ({ ...prev, associate_date: date }));
    };
    /** Handle to change TIME. */
    const handleDateTimeChange = (date) => {
        setStatus((prev) => ({ ...prev, associate_time: date }));
    };

    /** Handle to change key valuse(Container or Alias or BOL). */
    const handleRelatedDataChange = (event, val) => {
        if (isEdit) {
            const value = val?.id;
            setStatus((prev) => ({ ...prev, child_id: value ? value : 0, alias_id: '' }));
        } else {
            setStatus((prev) => ({ ...prev, child_id: val ? val : [], alias_id: '' }));
        }
    };
    /** Handle to change additional key values(Alias). */
    const handleAliasChange = (event, val) => {
        const value = val?.id;
        setStatus((prev) => ({ ...prev, alias_id: value ? value : '' }));
    };

    /** Handle to change status type.  */
    const handleStatusChange = (event, val) => {
        if (val) {
            const value = val.name;
            setStatus((prev) => ({
                ...prev,
                status_message_id: value ? value : '',
                associate_textfield: '',
                associate_location: '',
                associate_time: null,
                associate_date: null,
                message_id: val.id,
                afternoon: null,
            }));
            setAssociateValue(val.il_status_associate);
        }
    };

    /** Handle to change autocomplete Rail company and call Websocket to receive the searched result. */
    const handleStatusListChange = (event, params) => {
        readSocketData(dispatch, tabValue, 'cell', 'il_status_message', 'status_message_id', params, setStatusOptions);
    };

    /** Location List will be Websocket. */
    const handleLocationListChange = (event, params) => {
        readSocketData(dispatch, tabValue, 'cell', 'il_location', 'associate_location', params, setLocationList);
    };

    /** Handle to change location value.  */
    const handleLocationChange = (event, val) => {
        if (val) {
            setStatus((prev) => ({ ...prev, associate_location: val }));
        }
    };

    /** Handle to change TEXTFIELD. */
    const handleTextChange = (text) => {
        setStatus((prev) => ({ ...prev, associate_textfield: text }));
    };

    /**
     * Handle to update the status state based on checkbox selection.
     * Before noon : false | Afternoon : true |  neither: null
     */
    const handleAfternoonChange = (event) => {
        const isAfternoon = event.target.name === 'afternoon';
        setStatus((prevStatus) => ({
            ...prevStatus,
            afternoon: prevStatus.afternoon === isAfternoon ? null : isAfternoon,
        }));
    };

    /** Handle to change the toggle button for status visibility. */
    const handleShowChange = () => {
        setShow((prev) => !prev);
    };

    /** Handle to reset status state.  */
    const handleResetStatus = () => {
        setStatus(initialStatus());
        setIsEdit(false);
        setHasAlias(false);
        setStatusOptions([]);
        setAssociateValue('');
    };

    /**
     * When a colleague already deleted the entry this manager is deleting, hiding or editing, the
     * server answers STATUS_NOT_FOUND. Take that stale entry off the list and close its edit form,
     * so the manager is not left with a row that fails every time it is clicked.
     */
    const dropAlreadyDeletedStatus = (statusId) => (response) => {
        if (response?.error_code !== STATUS_NOT_FOUND) return;
        applySocketDelete(statusConfig.reducerActions[2], statusId, dispatch);
        handleResetStatus();
    };

    // History handlers
    /** Take an HTML snapshot of the status list to keep as edit history. */
    const captureStatusSnapshot = () => {
        const originalElement = document.getElementById('status-target-content');
        if (!originalElement) return null;

        const clonedElement = originalElement.cloneNode(true);
        clonedElement.querySelectorAll('button').forEach((btn) => btn.remove());

        const htmlWithoutButton = clonedElement.outerHTML;
        return DOMPurify.sanitize(htmlWithoutButton);
    };

    /**
     * Store a snapshot that was captured before the request was sent.
     * The server broadcasts the change to the requester as well, and that broadcast
     * arrives before the ack, so reading the DOM here would record the screen after
     * the change instead of the one the user is correcting from.
     */
    const saveStatusHistory = (rowId, snapshot) => {
        if (!snapshot) return;

        const childId = tabValue === 4 ? null : selectedHouseRow.id;
        const sendValue = { parent_id: rowId, child_id: childId, message: snapshot };
        createSocketData(dispatch, tabValue, 'row', 'il_status_history', '', sendValue);
    };

    /** Handle to set the anchor element for the history popover. */
    const handleHistoryClick = (event) => {
        const childId = tabValue === 4 ? null : selectedHouseRow.id;
        const sendValue = { parent_id: row.id, child_id: childId };
        const onSuccess = (data) => {
            if (data.length > 0) {
                setHistoryList((prev) => [...prev, data[0].data]);
                cursorRef.current = data[0].data.id ?? null;
                setHasPrev(Boolean(data[0].has_prev));
            }
        };
        readSocketData(dispatch, tabValue, 'row', 'il_status_history', '', sendValue, onSuccess);
        setHistoryAnchorEl(event.currentTarget);
    };

    /** Handle to close a history popover. */
    const handleHistoryClose = () => {
        setHistoryAnchorEl(null);
        setHistoryList([]);
        setIndexRef(0);
        cursorRef.current = null;
        setHasPrev(false);
    };

    /** Validate the status state before submission. */
    const validateStatus = (formattedStatus) => {
        const childIdInvalid =
            formattedStatus.child_id === '' ||
            (Array.isArray(formattedStatus.child_id) && formattedStatus.child_id.length === 0);

        if (!formattedStatus.event_date || childIdInvalid || formattedStatus.status_message_id === '') {
            dispatch(
                setSnackbar({
                    open: true,
                    message: 'Please fill out the required fields.',
                    severity: 'warning',
                }),
            );
            return false;
        }

        // "Warning Sign"(id=0)이 다른 항목과 함께 선택된 경우
        const childIdArr = formattedStatus.child_id;
        const isAllSelected =
            Array.isArray(childIdArr) &&
            childIdArr.some((item) => (typeof item === 'object' && item !== null ? item?.id === 0 : item === 0));
        const hasMultiple = Array.isArray(childIdArr) && childIdArr.length >= 2;
        if (isAllSelected && hasMultiple) {
            dispatch(
                setSnackbar({
                    open: true,
                    message: 'Cannot choose "Warning Sign" with other options',
                    severity: 'warning',
                }),
            );
            return false;
        }

        return true;
    };

    /** Handle to submit new status state to server. */
    const handleAddStatus = () => {
        // Prevent adding status if case_closure is Complete
        if (row?.case_closure === 'Complete') {
            dispatch(
                setSnackbar({
                    open: true,
                    message: 'Cannot modify fields when Case Closure is Complete. Please change Case Closure first.',
                    severity: 'warning',
                }),
            );
            return;
        }

        const reducerString = statusConfig.reducerActions[1];
        const transformedStatus = {
            ...status,
            house_id: statusConfig.type === 'trucking' ? 0 : selectedHouseRow.id,
            alias_id: resolveAliasId(status.child_id, status.alias_id),
        };

        // Format date fields as YYYY-MM-DD (date-only)
        if (transformedStatus.event_date) {
            const eventDate = dayjs.isDayjs(transformedStatus.event_date)
                ? transformedStatus.event_date.toDate()
                : transformedStatus.event_date;
            transformedStatus.event_date = formatDateToDateString(eventDate);
        }
        if (transformedStatus.afternoon !== null && transformedStatus.afternoon !== undefined) {
            // afternoon이 null이 아니면 (true 또는 false) - associate_time은 Null
            transformedStatus.associate_time = null;
        } else {
            if (transformedStatus.associate_time && transformedStatus.associate_date) {
                // afternoon이 Null이며 associate_time이 존재하는 경우 - TimePicker 값 사용
                // 담당자가 고른 약속 날짜와 시각을 근무지(LA) 기준 한 순간으로 묶는다.
                // 한국에서 14:30으로 입력해도 상태 문구와 LA 담당자 화면에 14:30으로 뜬다.
                transformedStatus.associate_time = combineOperationDateTime(
                    transformedStatus.associate_date,
                    transformedStatus.associate_time
                );
                transformedStatus.afternoon = null;
            } else {
                // afternoon과 associate_time 모두 Null인 경우 - associate_date만 사용
                transformedStatus.associate_time = null;
                transformedStatus.afternoon = null;
            }
        }

        if (transformedStatus.associate_date) {
            const associateDate = dayjs.isDayjs(transformedStatus.associate_date)
                ? transformedStatus.associate_date.toDate()
                : transformedStatus.associate_date;
            transformedStatus.associate_date = formatDateToDateString(associateDate);
        }

        if (
            statusConfig.type === 'trucking' &&
            typeof status.associate_location === 'string' &&
            status.associate_location.trim() !== ''
        ) {
            transformedStatus.associate_location = {
                id: randomId(),
                name: status.associate_location.trim(),
            };
        }

        if (validateStatus(status) && selectedHouseRow) {
            console.log('=== handleAddStatus: sending to server ===', transformedStatus);
            const snapshot = captureStatusSnapshot();
            createSocketData(
                dispatch,
                tabValue,
                'row',
                'il_status',
                reducerString,
                transformedStatus,
                () => {
                    handleResetStatus();
                    saveStatusHistory(row.id, snapshot);
                },
            );
        }
    };

    /** Handle to re-submit status state to server for updating. */
    const handleUpdateStatus = () => {
        // Prevent updating status if case_closure is Complete
        if (row?.case_closure === 'Complete') {
            dispatch(
                setSnackbar({
                    open: true,
                    message: 'Cannot modify fields when Case Closure is Complete. Please change Case Closure first.',
                    severity: 'warning',
                }),
            );
            return;
        }

        const reducerString = statusConfig.reducerActions[0];
        const transformedStatus = {
            ...status,
            alias_id: resolveAliasId(status.child_id, status.alias_id),
        };

        // Format date fields as YYYY-MM-DD (date-only)
        if (transformedStatus.event_date) {
            const eventDate = dayjs.isDayjs(transformedStatus.event_date)
                ? transformedStatus.event_date.toDate()
                : transformedStatus.event_date;
            transformedStatus.event_date = formatDateToDateString(eventDate);
        }

        if (transformedStatus.afternoon !== null && transformedStatus.afternoon !== undefined) {
            // afternoon이 null이 아니면 (true 또는 false) - associate_time은 Null
            transformedStatus.associate_time = null;
        } else {
            if (transformedStatus.associate_time && transformedStatus.associate_date) {
                // afternoon이 Null이며 associate_time이 존재하는 경우 - TimePicker 값 사용
                // 담당자가 고른 약속 날짜와 시각을 근무지(LA) 기준 한 순간으로 묶는다.
                // 한국에서 14:30으로 입력해도 상태 문구와 LA 담당자 화면에 14:30으로 뜬다.
                transformedStatus.associate_time = combineOperationDateTime(
                    transformedStatus.associate_date,
                    transformedStatus.associate_time
                );
                transformedStatus.afternoon = null;
            } else {
                // afternoon과 associate_time 모두 Null인 경우 - associate_date만 사용
                transformedStatus.associate_time = null;
                transformedStatus.afternoon = null;
            }
        }

        if (transformedStatus.associate_date) {
            const associateDate = dayjs.isDayjs(transformedStatus.associate_date)
                ? transformedStatus.associate_date.toDate()
                : transformedStatus.associate_date;
            transformedStatus.associate_date = formatDateToDateString(associateDate);
        }

        if (
            statusConfig.type === 'trucking' &&
            typeof status.associate_location === 'string' &&
            status.associate_location.trim() !== ''
        ) {
            transformedStatus.associate_location = {
                id: randomId(),
                name: status.associate_location.trim(),
            };
        }

        if (validateStatus(status) && selectedHouseRow) {
            console.log('=== handleUpdateStatus: sending to server ===', transformedStatus);
            const snapshot = captureStatusSnapshot();
            updateSocketData(
                dispatch,
                tabValue,
                'row',
                'il_status',
                reducerString,
                transformedStatus,
                () => {
                    handleResetStatus();
                    saveStatusHistory(row.id, snapshot);
                },
                dropAlreadyDeletedStatus(transformedStatus.id),
            );
        }
    };

    /** Handle to delete a specific status from the StatusList.  */
    const handleDeleteStatus = (indexToDelete) => {
        // Prevent deleting status if case_closure is Complete
        if (row?.case_closure === 'Complete') {
            dispatch(
                setSnackbar({
                    open: true,
                    message: 'Cannot modify fields when Case Closure is Complete. Please change Case Closure first.',
                    severity: 'warning',
                }),
            );
            return;
        }

        const reducerString = statusConfig.reducerActions[2];

        if (window.confirm('Are you sure you want to delete the entry?')) {
            const snapshot = captureStatusSnapshot();
            deleteSocketData(
                dispatch,
                tabValue,
                'row',
                'il_status',
                reducerString,
                indexToDelete,
                () => {
                    handleResetStatus();
                    saveStatusHistory(row.id, snapshot);
                },
                dropAlreadyDeletedStatus(indexToDelete),
            );
        }
    };

    /** Handle to hide a specific status from the StatusList.  */
    const handleHiddenStatus = (id) => {
        // Prevent hiding status if case_closure is Complete
        if (row?.case_closure === 'Complete') {
            dispatch(
                setSnackbar({
                    open: true,
                    message: 'Cannot modify fields when Case Closure is Complete. Please change Case Closure first.',
                    severity: 'warning',
                }),
            );
            return;
        }

        const reducerString = statusConfig.reducerActions[0];
        const findStatus = statusList.find((item) => item.id === id);
        // visibility_toggle tells the server this save comes from the Hide/Show button, not
        // from Edit → Update. Hiding or showing never recalculates ETA, LFD or Detention, even
        // when a colleague already flipped the same entry and this request repeats it.
        const submitData = { ...findStatus, hidden: !findStatus.hidden, visibility_toggle: true };

        // Format date fields as YYYY-MM-DD (date-only) if they exist
        if (submitData.event_date) {
            submitData.event_date = formatDateToDateString(submitData.event_date);
        }
        if (submitData.associate_date) {
            submitData.associate_date = formatDateToDateString(submitData.associate_date);
        }

        const confirmMessage = findStatus.hidden
            ? 'Are you sure you want to show the entry?'
            : 'Are you sure you want to hide the entry?';

        if (window.confirm(confirmMessage)) {
            updateSocketData(
                dispatch,
                tabValue,
                'row',
                'il_status',
                reducerString,
                submitData,
                handleResetStatus(),
                dropAlreadyDeletedStatus(id),
            );
        }
    };

    /** Handle to switch to edit mode for modifying a specific status. */
    const handleEditStatus = (id) => {
        const find_edit_status = statusList.find((item) => item.id === id);

        // A deactivated message is blocked at the Edit button (StatusRenderer disables it
        // with a tooltip), so this handler only ever receives editable entries.
        setAssociateValue(deriveEditAssociateValue(find_edit_status));
        setHasAlias(!!find_edit_status.alias_id);

        // Parse dates to local timezone to avoid timezone shift issues
        const parseDateForDayjs = (dateValue) => {
            if (!dateValue) return null;
            const localDate = parseDateToLocal(dateValue);
            return localDate ? dayjs(localDate) : null;
        };

        setStatus({
            id: id,
            shipment_type: find_edit_status.shipment_type,
            event_date: parseDateForDayjs(find_edit_status.event_date),
            parent_id: find_edit_status.parent_id,
            house_id: statusConfig.type === 'trucking' ? 0 : selectedHouseRow.id,
            child_id: find_edit_status.child_id,
            alias_id: find_edit_status.alias_id,
            status_message_id: find_edit_status.status_message_id,
            message_id: find_edit_status.message_id,
            associate_location:
                statusConfig.type === 'trucking'
                    ? find_edit_status.associate_location?.name || ''
                    : find_edit_status.associate_location,
            associate_textfield: find_edit_status.associate_textfield,
            associate_date: parseDateForDayjs(find_edit_status.associate_date),
            // 저장할 때 근무지 기준으로 묶었으므로, 수정하려고 다시 열었을 때도
            // 담당자가 입력했던 숫자가 그대로 떠야 한다.
            associate_time: toOperationTime(find_edit_status.associate_time),
            afternoon: find_edit_status.afternoon,
            hidden: find_edit_status.hidden,
            warning: find_edit_status.warning,
        });
        setIsEdit(true);
    };

    /**
     * Handle to put the status on every container of the house at once, or clear the selection.
     * A status for all containers is a container-level status, so a split picked earlier for a
     * single container is dropped together with that selection.
     */
    const handleAllSelectedChange = (cntrList, target) => {
        if (target) {
            const allSelectedList = cntrList.filter((item) => item.id !== 0);
            const filterBlankList = allSelectedList.filter((item) => item.name !== '');
            setStatus((prev) => ({ ...prev, child_id: filterBlankList, alias_id: '' }));
        } else {
            setStatus((prev) => ({ ...prev, child_id: [], alias_id: '' }));
        }
    };

    return {
        // Date and Time handlers
        handleEventDateChange,
        handleDateChange,
        handleDateTimeChange,

        // Related data handlers
        handleRelatedDataChange,
        handleAliasChange,

        // Status handlers
        handleStatusChange,
        handleStatusListChange,

        // Location handlers
        handleLocationListChange,
        handleLocationChange,

        // Text and afternoon handlers
        handleTextChange,
        handleAfternoonChange,

        // UI state handlers
        handleShowChange,
        handleResetStatus,

        // History handlers
        handleHistoryClick,
        handleHistoryClose,

        // CRUD handlers
        handleAddStatus,
        handleUpdateStatus,
        handleDeleteStatus,
        handleHiddenStatus,
        handleEditStatus,

        handleAllSelectedChange,
    };
};
